import { Account, Person, Asset, Liability } from "@models/estateProfile.model";
import {
  AgoPayload,
  RobMutationAccountData,
  RobMutationAssetData,
  RobMutationLiabilityData,
  RobMutationPersonData,
} from "@models/rob.model";
import { AccountType } from "../../types/AccountType";
import { LiabilityType } from "../../types/LiabilityType";
import { FormValidationService } from "../form-validation/form-validation.service";
import { WebLogger } from "@weps/shared-http";

export const DEFAULT_FINANCIAl_INSTITUTION_ID = "999999999999999";

// Create FormValidationService instance for use in functions
const formValidationService = new FormValidationService();

export const GET_CLIENT_QUERY = `query GetClientById
{
  estateProfile {
      estateProfileId
      vanillaSyncAllowed
      navigationIntent
      primaryClient {
          piClientId
          uuid
          firstName
          lastName
          relations {
              to
              relationType
          }
          city
          state
          country
          zip
          address
          dateOfBirth
          deceased
      }
      people {
           piClientId
           uuid
           firstName
           lastName
           city
           state
           country
           zip
           address
           dateOfBirth
           deceased
           relations {
              to
              relationType
          }
          isSpouse
       }
      accounts {
              accountId
              externalId
              accountName
              accountType
              ownerType
              balance
              inEstateProfile
              excludeFromInventory
              ownerPoid
              needsAttentionList {
                attentionType
                message
              }
          }
      manuallyEnteredOutsideAccounts {
              accountId
              externalId
              accountName
              ownerType
              ownerPoid
              ownerId
              jointType
              accountType
              balance
              inEstateProfile
              excludeFromInventory
              payOnDeath
              version
              beneficiaries {
                associatedMemberId
                type
                distributionPercentage
              }
              financialInstitutionId
              financialInstitutionName
           }
      manuallyEnteredOutsideAssets {
        assetId
        assetName
        assetType
        realEstateType
        ownerType
        ownerPoid
        ownerId
        jointType
        valuation
        valuationDate
        inEstateProfile
        excludeFromInventory
      }
      manuallyEnteredLiabilities {
        liabilityId
        liabilityName
        liabilityType
        assetId
        creditor
        valuation
        ownerType
        ownerId
        jointType
        valuationDate
        inEstateProfile
        excludeFromInventory
      }
      trusts {
        householdMemberId
        providedBy
        legalName
        displayName
        trustType
        settlors {
          memberType
          householdMemberId
        }
        referenceIds {
          clientPoid
        }
        source
      }
  }
}
`;

export const CREATE_ACCOUNT_MUTATION_QUERY = (agoPayload: string, mezPayload: string): string => `
    createAccounts(manualAccounts: [${agoPayload}],
    manualAccountsAdditionalInfo: [${mezPayload}]) {
      accounts {
        accountId
        accountType
        payOnDeath
        beneficiaries {
          associatedMemberId
          type
          distributionPercentage
        }
      }
    }`;

export const UPDATE_ACCOUNT_MUTATION_QUERY = (agoPayload: string, mezPayload: string): string => `
    updateAccount(
      agoInput: ${agoPayload},
      mezInput: ${mezPayload}
    )`;

export const CREATE_LIABILITY_MUTATION_QUERY = (formattedLiabilityPayload: string): string => `
    createLiabilities(input: [${formattedLiabilityPayload}]) {
      liabilityId
      liabilityName
      liabilityType
      valuation
      assetId
      creditor
      ownerId
      jointType
    }`;

export const UPDATE_ASSET_AND_LIABILITIES_MUTATION_QUERY = (input: string): string => `
    editAssetAndLiability(input: ${input}) {
      asset {
          assetId
          assetName
          assetType
          valuation
          jointType
        }
        liability {
          liabilityId
          liabilityName
          liabilityType
          assetId
          creditor
          jointType
        }
    }`;

export const CREATE_ASSET_MUTATION_QUERY = (formattedAssetPayload: string): string => `
    createAssets(input:[${formattedAssetPayload}]){
      assetId
    }`;

export const CREATE_ESTATE_PROFILE_MUTATION_QUERY = (formattedInput: string): string => `mutation {
    compressedEstateProfileCreate(input: ${formattedInput}) {
      statusCode
      message
      estateProfileId
    }
  }`;
export const SAVE_TRUST_MUTATION_QUERY = (formattedTrustPayload: string): string => `
    saveTrust(input: ${formattedTrustPayload}) {
      trust {
        householdMemberId
        displayName
        legalName
        trustType
        settlors {
          householdMemberId
          memberType
          allocationPercentage
          additionalDetails
        }
        referenceIds {
          clientPoid
        }
        state
        source
        isLinked
        needsReview
      }
    }`;

export const UPDATE_TRUST_MUTATION_QUERY = (formattedTrustPayload: string): string => `
    updateTrust(input: ${formattedTrustPayload}) {
      trust {
        householdMemberId
        displayName
        legalName
        trustType
        settlors {
          householdMemberId
          memberType
          allocationPercentage
          additionalDetails
        }
        referenceIds {
          clientPoid
        }
        state
        source
        isLinked
        needsReview
      }
    }`;

export const UPDATE_EXCLUDE_FROM_INVENTORY_MUTATION = (formattedInput: string): string => `
    updateExcludeFromInventory(input: ${formattedInput}) {
      statusCode
      message
    }`;

export const CREATE_ASSET_AND_LIABILITY_MUTATION_QUERY = (
  formattedAssetInput: string,
  formattedLiabilityInput: string,
): string => `
    createAssetAndLiability(assetInput: ${formattedAssetInput}, liabilityInput: ${formattedLiabilityInput}) {
      asset {
        assetId
        assetName
        assetType
        valuation
        jointType
      }
      liability {
        liabilityId
        liabilityName
        liabilityType
        assetId
        creditor
        jointType
      }
    }`;

export function getEstateProfileMutation(
  estateProfileName: string,
  primaryClient: RobMutationPersonData,
  people: RobMutationPersonData[],
  accounts: RobMutationAccountData[],
  assets: RobMutationAssetData[],
  liabilities: RobMutationLiabilityData[],
): string {
  const input = {
    estateProfileName: estateProfileName,
    primaryClient: primaryClient,
    accounts: accounts,
    assets: assets,
    liabilities: liabilities,
  };
  if (people.length > 0) input["people"] = people;

  const formattedInput = formatPayload(input);
  return CREATE_ESTATE_PROFILE_MUTATION_QUERY(formattedInput);
}

function formatPayload(payload: object, enumFields: string[] = []): string {
  // Using JSON.stringify to remove the quotations around the properties in the sent payloads, so they're not considered strings
  let formatted = JSON.stringify(payload).replace(/"([^"]+)":/g, "$1:");

  // Remove quotes from specified enum fields
  enumFields.forEach((field) => {
    const regex = new RegExp(`${field}:"([^"]+)"`, "g");
    formatted = formatted.replace(regex, `${field}:$1`);
  });

  return formatted;
}

// eslint-disable-next-line complexity,max-statements
export function getAccountMutationQuery(state: any): string {
  const currentAccountType = state["account-type"]["accountType"];
  const currentAccountInfo = state[currentAccountType];
  const ownerInfo = state["owner-type"];

  WebLogger.log({
    message: "getAccountMutationQuery called",
    payload: {
      currentAccountType: currentAccountType,
      currentAccountInfo: currentAccountInfo,
      ownerInfo: ownerInfo,
    },
  });

  WebLogger.log({
    message: "getAccountMutationQuery POD and beneficiary state",
    level: "info",
    payload: {
      event_type: "account_mutation_pod_state",
      pod_flag: currentAccountInfo.payableOnDeathDesignation,
      primary_count: currentAccountInfo.beneficiaryState?.primaryBeneficiaries?.length || 0,
      backup_count: currentAccountInfo.beneficiaryState?.backupBeneficiaries?.length || 0,
    },
  });

  /*
  TODO: For new trust owners, we'll need to handle creating the new trust either here or in ROB
  TODO: Also, for future types of owners, we'll need to add those here as well.
  */

  const isOtherInstitution =
    !currentAccountInfo.financialInstitutionId ||
    currentAccountInfo.financialInstitutionId === DEFAULT_FINANCIAl_INSTITUTION_ID;

  const agoPayload: AgoPayload = {
    financialInstitutionId: isOtherInstitution
      ? DEFAULT_FINANCIAl_INSTITUTION_ID
      : currentAccountInfo.financialInstitutionId,
    // Only include financialInstitutionName when the user selected "Other" — use the typed
    // institution name if provided, otherwise fall back to the account name.
    ...(isOtherInstitution && {
      financialInstitutionName:
        currentAccountInfo.financialInstitutionName || currentAccountInfo.accountName,
    }),
    //TRUST_OWNER is not a valid ownerType for AGO
    ownerType: ownerInfo.ownerType === "TRUST_OWNER" ? "PRIMARY" : ownerInfo.ownerType,
    accountType: currentAccountInfo.accountType,
    valuationDate: new Date().toISOString().split("T")[0],
    balance: {
      amount: parseFloat(formValidationService.sanitizeCurrencyInput(currentAccountInfo.balance)),
    },
  };

  const mezPayload: any = {
    ownerId: ownerInfo.ownerUuid,
    jointType: ownerInfo.jointType,
  };

  // Add POD and beneficiary data to MEZ payload
  WebLogger.log({
    message: "Checking payableOnDeathDesignation before setting mezPayload.payOnDeath",
    level: "info",
    payload: {
      payableOnDeathDesignation: currentAccountInfo.payableOnDeathDesignation,
      hasBeeneficiaryState: !!currentAccountInfo.beneficiaryState,
      isEdit: !!currentAccountInfo.isEdit,
    },
  });

  // Set payOnDeath based on checkbox state, independent of beneficiary existence
  if (currentAccountInfo.payableOnDeathDesignation) {
    mezPayload.payOnDeath = true;

    // Add beneficiaries if they exist
    if (currentAccountInfo.beneficiaryState) {
      const beneficiaryState = currentAccountInfo.beneficiaryState;
      const allBeneficiaries = [
        ...(beneficiaryState.primaryBeneficiaries || []),
        ...(beneficiaryState.backupBeneficiaries || []),
      ];

      // Only include beneficiaries that have a valid householdMemberId from ZOK
      const validBeneficiaries = allBeneficiaries.filter((b) => b.householdMemberId);
      const skippedCount = allBeneficiaries.length - validBeneficiaries.length;

      if (skippedCount > 0) {
        WebLogger.log({
          message: `MEZ Payload skipping ${skippedCount} beneficiaries with null householdMemberId`,
          level: "warn",
          payload: { event_type: "mez_payload_skipped_benes", skipped_count: skippedCount },
        });
      }

      mezPayload.beneficiaries = validBeneficiaries.map((b) => ({
        associatedMemberId: b.householdMemberId,
        type: b.type,
        distributionPercentage: Math.round(b.distributionPercentage),
      }));

      WebLogger.log({
        message: "MEZ Payload beneficiaries added",
        level: "info",
        payload: {
          event_type: "mez_payload_benes_added",
          beneficiary_count: mezPayload.beneficiaries.length,
          beneficiary_ids: mezPayload.beneficiaries.map((b: any) => b.associatedMemberId),
        },
      });
    }
  } else {
    // POD is unchecked - set to false and clear beneficiaries
    mezPayload.payOnDeath = false;
    // Only clear beneficiaries in edit mode to avoid sending empty array in create
    if (currentAccountInfo.isEdit) {
      mezPayload.beneficiaries = [];
    }
  }

  WebLogger.log({ message: "MEZ Payload created", payload: { mezPayload } });

  // The edit/update mutation needs the accountId sent in the payload to know which account is getting updated; MEZ Payload also need the accountType as a minimum
  if (currentAccountInfo.isEdit) {
    agoPayload["accountId"] = currentAccountInfo.accountId;
    mezPayload["accountId"] = currentAccountInfo.accountId;
    mezPayload["accountType"] = currentAccountInfo.accountType;

    if (currentAccountInfo.excludeFromInventory !== undefined) {
      mezPayload["excludeFromInventory"] = currentAccountInfo.excludeFromInventory;
    }

    // Optimistic locking is handled by the PUT lambda, no need to send version
  }

  // TODO: Just a quick one to handle bank accounts for the time being, need to update/remove this later when we move all the mapping into ROB
  if (currentAccountType === "bank-accounts") {
    mezPayload["accountType"] = "BANK_ACCOUNT";
  }

  WebLogger.log({ message: "AGO Payload created", payload: { agoPayload } });
  WebLogger.log({ message: "MEZ Payload final state", payload: { mezPayload } });

  const formattedAgoPayload = formatPayload(agoPayload);
  const formattedMezPayload = formatPayload(mezPayload);

  WebLogger.log({
    message: "Formatted payloads",
    payload: {
      formattedAgoPayload: formattedAgoPayload,
      formattedMezPayload: formattedMezPayload,
    },
  });

  // If the isEdit field exists (account has been put through the edit flow), then call the update mutation query. If not, then call the create mutation query
  const mutationQuery = currentAccountInfo.isEdit
    ? UPDATE_ACCOUNT_MUTATION_QUERY(formattedAgoPayload, formattedMezPayload)
    : CREATE_ACCOUNT_MUTATION_QUERY(formattedAgoPayload, formattedMezPayload);

  return mutationQuery;
}

export function editAssetAndLiabilityMutationQuery(state: any): { query: string } {
  const currentAssetType = state["account-type"]["accountType"];
  const currentAssetInfo = state[currentAssetType];
  const ownerInfo = state["owner-type"];

  // TODO: remap this in ROB but doing this since MEZ expects valuation instead of balance.
  currentAssetInfo.valuation = currentAssetInfo.balance;

  const filteredAssetInfo = {
    assetId: currentAssetInfo.assetId,
    assetName: currentAssetInfo.accountName,
    assetType: "REAL_ESTATE",
    ownerId: ownerInfo.ownerUuid,
    realEstateType: currentAssetInfo.accountType,
    valuation: parseFloat(formValidationService.sanitizeCurrencyInput(currentAssetInfo.balance)),
    accountOwner: ownerInfo.ownerType,
    jointType: ownerInfo.jointType,
    ...(currentAssetInfo.liabilityId && { liabilityId: currentAssetInfo.liabilityId }),
    ...(currentAssetInfo.mortgageAmount != null && {
      mortgageAmount: parseFloat(
        formValidationService.sanitizeCurrencyInput(currentAssetInfo.mortgageAmount),
      ),
    }),
  };

  // Filter out currentAssetInfo unnecessary fields based on Asset and Liability model from estateProfile.model.ts
  const input = formatPayload(filteredAssetInfo);
  const query = UPDATE_ASSET_AND_LIABILITIES_MUTATION_QUERY(input);
  return { query: query };
}
export function getAssetMutationQuery(state: any): string {
  const assetType = state["account-type"]["accountType"];
  const subAssetType = state[assetType]["accountType"];
  const currentAccountInfo = state[assetType];
  const ownerInfo = state["owner-type"];

  const assetPayload = {
    assetName: currentAccountInfo.accountName,
    valuation: parseFloat(formValidationService.sanitizeCurrencyInput(currentAccountInfo.balance)),
    assetType: "REAL_ESTATE", // Hardcoding since this is the only asset type we are currently supporting
    realEstateType: subAssetType,
    ownerId: ownerInfo.ownerUuid,
    jointType: ownerInfo.jointType,
    valuationDate: new Date().toISOString(),
  };

  const formattedAssetPayload = JSON.stringify(assetPayload)
    .replace(/"([^"]+)":/g, "$1:")
    .replace(/"(REAL_ESTATE)"/, "$1")
    .replace(`"${subAssetType}"`, subAssetType);

  return CREATE_ASSET_MUTATION_QUERY(formattedAssetPayload);
}

export function getAssetAndLiabilityMutationQuery(state: any): string {
  try {
    WebLogger.log({ message: "Building Asset and Liability mutation query" });
    const assetType = state["account-type"]["accountType"];
    const subAssetType = state[assetType]["accountType"];
    const currentAccountInfo = state[assetType];
    const ownerInfo = state["owner-type"];

    const assetInput = {
      assetName: currentAccountInfo.accountName,
      valuation: parseFloat(
        formValidationService.sanitizeCurrencyInput(currentAccountInfo.balance),
      ),
      assetType: "REAL_ESTATE",
      realEstateType: subAssetType,
      ownerId: ownerInfo.ownerUuid,
      jointType: ownerInfo.jointType,
      valuationDate: new Date().toISOString(),
    };

    const liabilityInput = {
      liabilityName: currentAccountInfo.accountName,
      liabilityType: "MORTGAGE",
      valuation: parseFloat(
        formValidationService.sanitizeCurrencyInput(currentAccountInfo.mortgageAmount),
      ),
      ownerId: ownerInfo.ownerUuid,
      jointType: ownerInfo.jointType,
      valuationDate: new Date().toISOString(),
      creditor: currentAccountInfo.accountName,
    };

    const formattedAssetInput = JSON.stringify(assetInput)
      .replace(/"([^"]+)":/g, "$1:")
      .replace(/"REAL_ESTATE"/g, "REAL_ESTATE")
      .replace(/"PERSONAL"/g, "PERSONAL")
      .replace(/"INVESTMENT"/g, "INVESTMENT");

    const formattedLiabilityInput = JSON.stringify(liabilityInput)
      .replace(/"([^"]+)":/g, "$1:")
      .replace(/liabilityType:\s*MORTGAGE/g, 'liabilityType:"MORTGAGE"');

    WebLogger.log({
      message: "Asset input formatted",
      payload: { formattedAssetInput: formattedAssetInput },
    });
    WebLogger.log({
      message: "Liability input formatted",
      payload: { formattedLiabilityInput: formattedLiabilityInput },
    });

    return CREATE_ASSET_AND_LIABILITY_MUTATION_QUERY(formattedAssetInput, formattedLiabilityInput);
  } catch (error) {
    WebLogger.log({
      message: "Query service getAssetAndLiabilityMutationQuery error",
      level: "error",
      payload: {
        error:
          error instanceof Error ? { message: error.message, stack: error.stack } : String(error),
      },
    });
    throw error;
  }
}
function mapToLiabilityType(accountType: AccountType): LiabilityType {
  switch (accountType) {
    case AccountType.MORTGAGE:
      return LiabilityType.MORTGAGE;
    default:
      return LiabilityType.PAYABLES;
  }
}

// eslint-disable-next-line max-statements, complexity
export function getLiabilityMutationQuery(state: any): string {
  const accountType = state["account-type"]["accountType"];
  const currentAccountInfo = state[accountType];
  const ownerInfo = state["owner-type"];

  // Sanitize the valuation field to ensure it is a numeric value
  const sanitizedValuation = currentAccountInfo.balance
    ? parseFloat(formValidationService.sanitizeCurrencyInput(currentAccountInfo.balance))
    : 0;

  WebLogger.log({
    message: "Liability data prepared for sending",
    payload: { liabilityType: mapToLiabilityType(currentAccountInfo.accountType) },
  });

  const liabilityPayload = {
    creditor: currentAccountInfo.accountName,
    liabilityName: currentAccountInfo.accountName,
    liabilityType: mapToLiabilityType(currentAccountInfo.accountType),
    ownerId: ownerInfo.ownerUuid,
    valuation: sanitizedValuation,
    valuationDate: new Date().toISOString(),
  };

  const formattedLiabilityPayload = formatPayload(liabilityPayload);
  WebLogger.log({
    message: "Liability payload formatted",
    payload: { formattedLiabilityPayload: formattedLiabilityPayload },
  });

  const mutationQuery = CREATE_LIABILITY_MUTATION_QUERY(formattedLiabilityPayload);
  WebLogger.log({
    message: "Liability mutation query created",
    payload: { mutationQuery: typeof mutationQuery === "string" ? mutationQuery : mutationQuery },
  });

  return CREATE_LIABILITY_MUTATION_QUERY(formattedLiabilityPayload);
}

function mapSettlorToEnum(settlor: string): string {
  const settlorMap: Record<string, string> = {
    PWH: "PRIMARY",
    SECONDARY: "SPOUSE",
    JOINT: "JOINT",
  };
  return settlorMap[settlor] || settlor;
}

export function buildTrustPayload(trustInfo: any, settlor: string, trustType: string): any {
  const payload: any = {
    householdMemberId: trustInfo.ownerUuid,
    settlor: settlor,
    trustType: trustType,
    displayName: trustInfo.trustDisplayName,
  };

  // legalName is only for CREATE - it's immutable and cannot be updated
  // Unless it's a Manually Entered trust that doesn't have a POID
  if (!trustInfo.isUpdate || !trustInfo.trustPoid) {
    payload.legalName = trustInfo.trustLegalName;
  }

  if (trustInfo.trustPoid) {
    payload.trustPoid = trustInfo.trustPoid;
  }

  return payload;
}

// eslint-disable-next-line complexity,max-statements
export function getTrustMutationQuery(state: any): string {
  const trustInfo: any = state["owner-type"];

  if (!trustInfo) {
    throw new Error("Trust information is missing from state");
  }

  if (!trustInfo.settlor) {
    throw new Error("Settlor is required but not provided");
  }

  const settlor = mapSettlorToEnum(trustInfo.settlor);
  const trustPayload = buildTrustPayload(trustInfo, settlor, trustInfo.trustType);
  const formattedTrustPayload = formatPayload(trustPayload, ["settlor", "trustType"]);

  return trustInfo.isUpdate
    ? UPDATE_TRUST_MUTATION_QUERY(formattedTrustPayload)
    : SAVE_TRUST_MUTATION_QUERY(formattedTrustPayload);
}

export function getInstitutionSearchQuery(searchTerm: string): {
  query: string;
  variables: { term: string };
} {
  return {
    query: `query SearchInstitutions($term: String!) {
      searchFinancialInstitutions(searchTerm: $term) {
        financialInstitutions {
          financialInstitutionId
          financialInstitutionName
        }
      }
    }`,
    variables: { term: searchTerm },
  };
}

export function mapToRobMutationPersonData(person: Person): RobMutationPersonData {
  return {
    piClientId: person.piClientId,
    uuid: person.uuid,
    isSpouse: person.isSpouse,
    relations: person.relations?.filter((r) => r.relationshipType === "MARRIED_TO"),
  };
}

export function mapToRobMutationAccountData(account: Account): RobMutationAccountData {
  return {
    accountId: account.accountId,
    externalId: account.externalId,
    inEstateProfile: account.inEstateProfile ?? false,
    excludeFromInventory: account.excludeFromInventory ?? false,
  };
}

export function mapToRobMutationAssetData(asset: Asset): RobMutationAssetData {
  return {
    assetId: asset.assetId,
    externalAssetId: asset.externalAssetId,
    inEstateProfile: asset.inEstateProfile ?? false,
    excludeFromInventory: asset.excludeFromInventory ?? false,
  };
}

export function mapToRobMutationLiabilityData(liability: Liability): RobMutationLiabilityData {
  return {
    liabilityId: liability.liabilityId,
    externalLiabilityId: liability.externalLiabilityId,
    inEstateProfile: liability.inEstateProfile ?? false,
    excludeFromInventory: liability.excludeFromInventory ?? false,
  };
}
